<template>
  <div>
    <p v-if="name"
      :class="{isRequired: isRequired}"
      class="optionLabel">
      {{ name[lang] }}
    </p>
    <div :class="{activeSubOption: !isSubOption && getActiveSubOption}">
      <select
        class="input select"
        v-model="selected"
        @change="setCheck">
        <option
          v-if="!optionInfo.isRequired"
          value="">
          {{ locale.select_none }}
        </option>
        <option
          v-for="(opt, index) in optionItems"
          :key="index"
          :value="opt.optionItemId"
          :disabled="getItemStatus(opt.status)">
          {{ getOptionName(opt) + (opt.price && opt.price > 0 ? ` (${priceFormat(opt.price)})` : '') }}
          {{ getItemStatus(opt.status) && `- ${getItemStatus(opt.status)}` }}
        </option>
      </select>
    </div>
    <div v-if="getActiveSubOption && subOption && subOption.subOption" class="shopSubOption">
      <div
        v-for="(opt, idx) in subOption.subOption"
        :key="idx">
        <ItemOption
          class="shopOptionItem isActive"
          :locale="locale"
          :lang="lang"
          :shopResponsive="shopResponsive"
          :uniqueId="opt.uniqueId"
          :shopItemId="shopItemId"
          :option="opt"
          :isSubOption="true" />
      </div>
    </div>
  </div>
</template>

<script>
import regExp from '@/common/regExp'

export default {
  name: 'OptionSelect',
  props: [
    'locale',
    'lang',
    'shopItemId',
    'shopResponsive',
    'uniqueId',
    'optionInfo',
    'isSubOption',
    'cartOptionData'
  ],
  components: {
    ItemOption: () => import('../ItemOption.vue')
  },
  computed: {
    isStore () {
      return this.$route.name === 'store'
    },
    siteCommon () {
      return this.$store.getters['common/getSetting']
    },
    selectedItems () {
      const code = this.siteCommon.currency.toUpperCase()
      const itemProperty = this.itemProperty
      const isReplaceOptionPrice = itemProperty && itemProperty.replaceOptionPrice
      function mergeOptionItemNames(itemNameObj = {}, optionItemNameObj = {}) {
        const allKeys = new Set([
          ...Object.keys(itemNameObj),
          ...Object.keys(optionItemNameObj)
        ]);

        const result = {};

        allKeys.forEach(key => {
          const item = itemNameObj[key] || '';
          const option = optionItemNameObj[key] || '';

          if (item && option) {
            result[key] = `${item} (${option})`;
          } else if (item) {
            result[key] = item;
          } else if (option) {
            result[key] = `(${option})`;
          } else {
            result[key] = ''; // 둘 다 비었으면 빈 문자열
          }
        });

        return result;
      }
      return this.optionItems.filter(item => this.selected.includes(item.optionItemId)).map(item => {
        return {
          optionItemId: item.optionItemId,
          name: isReplaceOptionPrice ? mergeOptionItemNames(itemProperty.shopItemName, item.name) : item.name,
          quantity: item.quantity,
          ...item.shopItemId && {
            shopItemId: item.shopItemId
          },
          ...item.price && {
            price: item.price,
            totalPrice: regExp.priceTotal(item.price, item.quantity, code)
          },
          ...item.pId && {
            pId: item.pId
          }
        }
      })
    },
    getActiveSubOption () {
      return this.$store.getters['shop/getActiveSubOption']
    }
  },
  created () {
    this.name = this.optionInfo.name
    this.isRequired = this.optionInfo.isRequired
    this.optionItems = this.optionInfo.optionItems.map(x => {
      return {
        ...x,
        quantity: 1
      }
    })
    this.selected = this.optionInfo.selected
    if (this.cartOptionData) {
      this.optionItems = this.optionItems.map(x => {
        const cartOptionItem = this.cartOptionData.optionItems.find(o => o.optionItemId === x.optionItemId)
        return {
          ...x,
          ...cartOptionItem && {
            quantity: cartOptionItem.quantity
          }
        }
      })
      this.selected = this.cartOptionData.optionItems[0].optionItemId || ''
    }
  },
  watch: {
    selected (selected) {
      const data = {
        uniqueId: this.uniqueId,
        isValid: true,
        optionItems: this.selectedItems
      }
      this.$store.commit('shop/setDetailOptions', [data])
    }
  },
  data () {
    return {
      name: {},
      isRequired: false,
      optionItems: [],
      selected: '',
      subOption: null
    }
  },
  methods: {
    getItemStatus (presetId) {
      const getStatus = this.$store.getters['shop/getItemStatus'](presetId)
      const status = (getStatus && getStatus.presetOptions[0]) || null
      return status && status.text && status.text[this.lang]
    },
    priceFormat (price) {
      const currency = this.$store.getters['shop/getShopCurrency'] || null
      return this.$store.getters['util/getPriceFormatter'](price, currency)
    },
    getOptionName (opt) {
      const lang = this.lang
      const optionItemName = opt.name[lang]
      const itemProperty = this.itemProperty
      const isReplaceOptionPrice = itemProperty && itemProperty.replaceOptionPrice
      const isSubOption = this.isSubOption
      return optionItemName
    },
    async setCheck () {
      await this.$nextTick() // 비동기 기다리기
      let subOption = null
      this.subOption = null

      const optionItemId = this.selected
      const optionItem = this.optionItems && this.optionItems.find(x => x.optionItemId === optionItemId)
      const subOptionIds = optionItem && optionItem.subOption && optionItem.subOption.map(x => x.optionItemId)

      if (!this.isSubOption && subOptionIds && subOptionIds.length > 0) {
        const reqSubOption = await this.$store.dispatch('shop/getStoreSubOptionItems', {
          optId: this.optionInfo.id,
          selectedOptionItemId: optionItemId,
          subIds: subOptionIds
        })

        const rawOptionItems = reqSubOption && reqSubOption.message && reqSubOption.message.optionItem

        if (rawOptionItems && typeof rawOptionItems === 'object') {
          // 옵션 스토어에 실재하는 그룹만 필터
          const validOptionItems = Object.fromEntries(
            Object.entries(rawOptionItems).filter(([key, value]) => {
              return (
                value &&
                typeof value === 'object' &&
                Object.keys(value).length > 0 &&
                value.optId
              )
            })
          )

          subOption = Object.keys(validOptionItems).length > 0 ? validOptionItems : null
        }
      }

      const subOptionData = subOption
        ? this.$store.getters['shop/getOptionListData'](this.shopPageId, this.shopItemId, Object.values(subOption), this.optionInfo.id, optionItemId)
        : null

      this.subOption = {
        optionId: this.optionInfo.id,
        optionItemId: optionItemId,
        subOption: subOptionData
      }

      if (optionItemId && subOptionData) {
        this.setDetailSubOption(subOptionData)
        this.$store.commit('shop/setActiveSubOption', `${this.optionInfo.id}_unique_${optionItemId}`)
      }
    },
    setDetailSubOption (data) {
      const subOptionData = data.map(x => {
        const isSelect = x.type === 'select'
        return {
          ...x.id && { // 스토어의 option.id + 프리셋의 optionIds[].id
            id: x.id
          },
          ...x.optionId && { // 상품에서 직접등록한 option
            optionId: x.optionId
          },
          parentOption: data.optionId,
          name: x.name,
          uniqueId: x.uniqueId,
          isValid: !x.isRequired,
          optionItems: x.optionItems.filter((o, i) => o.isDefault || (isSelect && i === 0)).map(o => {
            return {
              id: o.id,
              quantity: o.quantity,
              optionItemId: o.optionItemId,
              ...o.price && {
                price: o.price,
                totalPrice: o.price
              },
              ...o.pId && {
                pId: o.pId
              }
            }
          })
        }
      })
      this.$store.commit('shop/setDetailOptions', subOptionData)
    },
  }
}
</script>